import Joi from '@hapi/joi';
import React from 'react';
import { connect } from 'react-redux';
import Select from 'react-select';
import { calculateDefectScore } from '../../../../../common/src/domain/defect/calculateDefectScore';
import { ITranslateProps, translate } from '../../../mixins/translate';
import { ILanguageCodeProps, IValidationComponentState, ValidatingComponent } from '../../../mixins/validation/validation';
import {
    ramsAOptions,
    ramsEcOptions,
    ramsEnvOptions,
    ramsROptions,
    ramsSOptions,
    getTotalPriorityValue,
    getWeightedPriorityValue,
} from '../../../utils/data/rams';
import { InspectionStandardTypes } from '../../../utils/enums/InspectionStandardTypes';
import { isEemuaOrApi } from '../../../utils/isEemuaOrApi';
import { reactSelectDefaultStyles } from '../../../utils/reactSelectDefaultStyles';
import { LocalizedDatePicker } from '../../common/LocalizedDatePicker';
import { EditPanel, EditPanelButton, EditPanelButtons, EditPanelCell, EditPanelInput, EditPanelRow, EditPanelSelect } from '../../elements';
import { ConditionScoreLabel } from '../../utils/ConditionScoreLabel';
import { Defect } from '../Interfaces/Defect';
import { INenDefect } from '../Interfaces/INenDefect';
import { RamssheepDropdownNen } from './RamsDropdownNEN';

interface IDefectAddEditProps extends ITranslateProps, ILanguageCodeProps {
    defect?: undefined | Defect;
    mode: 'edit' | 'add';
    inspectionStandardType: InspectionStandardTypes;
    nenDefects: INenDefect[];
    isManifestation: boolean;
    useCareScore: boolean;
    languageCode: string;
    onSave: (defect: Defect) => void;
    onCancel: () => void;
}

interface IDefectAddEditState extends IValidationComponentState {
    id?: undefined | string;
    defectType?: undefined | string;
    name?: undefined | string;
    code?: undefined | string;
    description?: undefined | string;
    seriousness?: undefined | number;
    intensity?: undefined | number;
    extend?: undefined | number;
    score?: undefined | number;
    careScore?: undefined | number;
    repairAdviceCategory?: undefined | string;
    repairDate?: undefined | Date;
    hideSeriousness?: undefined | boolean;
    hideIntensity?: undefined | boolean;
    hideExtend?: undefined | boolean;
    amount?: undefined | number;
    amountTotal?: undefined | number;
    measuringUnit?: undefined | string;
    measuringUnitAbbreviation?: undefined | string;
    cause?: undefined | string;
    aspect?: undefined | string;
    repairAdvice?: undefined | string;
    locationDescription?: undefined | string;
    details?: undefined | string;
    riscLevel?: undefined | string;
    ramsR?: undefined | string;
    ramsA?: undefined | string;
    ramsS?: undefined | string;
    ramsEc?: undefined | string;
    ramsEnv?: undefined | string;
    ramsTotalPriority?: undefined | string;
    ramsWeightedPriority?: undefined | string;
}

function mapStateToProps(state: any) {
    const { language } = state;
    return {
        languageCode: language.code,
    };
}

class DefectAddEdit extends ValidatingComponent<IDefectAddEditProps, IDefectAddEditState> {
    constructor(props: IDefectAddEditProps) {
        super(props);

        this.state = {
            submitting: false,
            failed: false,
            saved: true,
            errors: {},
        };
    }

    // eslint-disable-next-line @typescript-eslint/naming-convention
    public override UNSAFEcomponentWillReceiveProps(nextProps: IDefectAddEditProps) {
        if (this.props.defect) {
            if (!nextProps.defect) {
                this.prepareComponentState(undefined);
            } else {
                if (nextProps.defect.id !== this.props.defect.id) {
                    this.prepareComponentState(nextProps.defect);
                }
            }
        } else {
            if (nextProps.defect) {
                this.prepareComponentState(nextProps.defect);
            }
        }
        if (nextProps.nenDefects.length === 0) {
            void this.setState({ defectType: 'custom' });
        }
        if (nextProps.languageCode === 'en') {
            void this.setState({ defectType: 'custom' });
        }
    }

    public override componentDidMount() {
        this.prepareComponentState(this.props.defect);
        if (this.props.nenDefects.length === 0) {
            void this.setState({ defectType: 'custom' });
        }
    }

    public prepareComponentState(defect?: Defect) {
        const state = {} as IDefectAddEditState;
        if (defect) {
            state.id = defect.id;
            state.code = defect.code;
            state.name = defect.name;
            state.description = defect.description;
            state.seriousness = defect.seriousness;
            state.intensity = defect.intensity;
            state.extend = defect.extend;
            state.score = defect.score;
            state.careScore = defect.careScore;
            state.amount = defect.amount;
            state.amountTotal = defect.amountTotal;
            state.measuringUnit = defect.measuringUnit;
            state.measuringUnitAbbreviation = defect.measuringUnitAbbreviation;
            state.cause = defect.cause;
            state.aspect = defect.aspect;
            state.repairAdvice = defect.repairAdvice;
            state.locationDescription = defect.locationDescription;
            state.details = defect.details;
            state.riscLevel = defect.riscLevel;
            state.repairAdviceCategory = defect.repairAdviceCategory;
            state.ramsR = defect.ramsR;
            state.ramsA = defect.ramsA;
            state.ramsS = defect.ramsS;
            state.ramsEc = defect.ramsEc;
            state.ramsEnv = defect.ramsEnv;
            state.ramsWeightedPriority = defect.ramsWeightedPriority;
            if (defect.repairDate) {
                state.repairDate = new Date(defect.repairDate);
            } else {
                state.repairDate = undefined;
            }

            const nenDefect = this.getNenDefect(defect.code);
            if (nenDefect) {
                if (nenDefect.seriousness) {
                    state.hideSeriousness = true;
                }
                if (nenDefect.intensity) {
                    state.hideIntensity = true;
                }
                if (nenDefect.unitAbbreviation === 'st' && this.props.isManifestation) {
                    state.hideExtend = true;
                }
                state.defectType = nenDefect.defectType;
                state.measuringUnit = nenDefect.unit;
                state.measuringUnitAbbreviation = nenDefect.unitAbbreviation;
            } else {
                state.defectType = 'custom';
            }
        } else {
            state.id = undefined;
            state.code = '';
            state.name = '';
            state.description = '';
            state.seriousness = undefined;
            state.intensity = undefined;
            state.extend = undefined;
            state.defectType = '';
            state.repairDate = undefined;
            state.repairAdviceCategory = 'noRepair';
        }
        void this.setState(state);
    }

    public getValidatorTypes(): any {
        if (isEemuaOrApi(this.props.inspectionStandardType)) {
            return {
                repairDate: Joi.date().allow(null).label(this.props.t('repairAdvice.repairDate')),
            };
        }
        return {
            // defectType: Joi.string().required().label(this.props.t('common.defect')),
            amount: Joi.number().allow(null),
            amountTotal: Joi.number().allow(null),
            name: Joi.string().required().label(this.props.t('common.name')),
        };
    }

    public getValidatorData() {
        return {
            id: this.state.id,
            defectType: this.state.defectType,
            name: this.state.name,
            code: this.state.code,
            description: this.state.description,
            seriousness: this.state.seriousness,
            intensity: this.state.intensity,
            extend: this.state.extend,
            score: this.state.score,
            careScore: this.state.careScore,
            repairAdviceCategory: this.state.repairAdviceCategory,
            repairDate: this.state.repairDate,
            amount: this.state.amount,
            amountTotal: this.state.amountTotal,
            measuringUnit: this.state.measuringUnit,
            measuringUnitAbbreviation: this.state.measuringUnitAbbreviation,
            cause: this.state.cause,
            aspect: this.state.aspect,
            repairAdvice: this.state.repairAdvice,
            locationDescription: this.state.locationDescription,
            details: this.state.details,
            riscLevel: this.state.riscLevel,
            ramsR: this.state.ramsR,
            ramsA: this.state.ramsA,
            ramsS: this.state.ramsS,
            ramsEc: this.state.ramsEc,
            ramsEnv: this.state.ramsEnv,
            ramsTotalPriority: getTotalPriorityValue(
                this.state.ramsR,
                this.state.ramsA,
                this.state.ramsS,
                undefined,
                this.state.ramsEc,
                this.state.ramsEnv,
                undefined,
            ).toString(),
            ramsWeightedPriority: getWeightedPriorityValue(
                this.state.ramsR,
                this.state.ramsA,
                this.state.ramsS,
                undefined,
                this.state.ramsEc,
                this.state.ramsEnv,
                undefined,
            ),
        };
    }

    public override render() {
        // const isEemuaOrApi = (this.props.inspectionStandardType === InspectionStandardTypes.eemua159 || this.props.inspectionStandardType === InspectionStandardTypes.api653);
        const isNen = this.props.inspectionStandardType === InspectionStandardTypes.Nen2767;
        const weightedPriorityValue = getWeightedPriorityValue(
            this.state.ramsR,
            this.state.ramsA,
            this.state.ramsS,
            undefined,
            this.state.ramsEc,
            this.state.ramsEnv,
            undefined,
        );
        return (
            <EditPanel dark={true}>
                {this.props.nenDefects.length > 0 && this.props.languageCode === 'nl' && !isEemuaOrApi(this.props.inspectionStandardType) && (
                    <EditPanelRow>
                        <div className="col-md-12">
                            <label className="field">
                                <Select
                                    isClearable={true}
                                    styles={reactSelectDefaultStyles}
                                    placeholder={this.props.t('common.selectItem', this.props.t('common.defect'))}
                                    name="form-field-name"
                                    value={this.nenDefectsToOptions(this.props.nenDefects).find((i) => i.value === this.state.code)}
                                    options={this.nenDefectsToOptions(this.props.nenDefects)}
                                    onChange={async (option: any) => this.handleChange(option ? option.value : undefined, 'code')}
                                />
                            </label>
                        </div>
                        {this.renderHelpText(this.getValidationMessages('defectType'))}
                    </EditPanelRow>
                )}

                {(this.state.defectType === 'custom' || this.props.languageCode !== 'nl') && !isEemuaOrApi(this.props.inspectionStandardType) && (
                    <EditPanelRow>
                        <EditPanelCell rowCount={8}>
                            <EditPanelInput
                                name="name"
                                placeholder={this.props.t('common.name')}
                                value={this.state.name}
                                handleChange={this.handleChange.bind(this)}
                            />
                        </EditPanelCell>
                        <EditPanelCell rowCount={4}>
                            <EditPanelInput
                                name="code"
                                placeholder={this.props.t('common.code')}
                                value={this.state.code}
                                handleChange={this.handleChange.bind(this)}
                            />
                        </EditPanelCell>
                    </EditPanelRow>
                )}
                {(this.state.defectType === 'custom' || this.props.languageCode !== 'nl' || isEemuaOrApi(this.props.inspectionStandardType)) && (
                    <EditPanelRow>
                        <div className="col-md-12 pt10 custom-failure">
                            <EditPanelInput
                                name="description"
                                placeholder={
                                    isEemuaOrApi(this.props.inspectionStandardType)
                                        ? this.props.t('repairAdvice.description')
                                        : this.props.t('defect.description')
                                }
                                value={this.state.description}
                                handleChange={this.handleChange.bind(this)}
                            />
                        </div>
                    </EditPanelRow>
                )}

                {/* EEMUA 159 API 653 */}
                {isEemuaOrApi(this.props.inspectionStandardType) && (
                    <EditPanelRow>
                        <div className="col-md-6">
                            <label className="field select">
                                <select
                                    id="emua_catgory"
                                    name="defect_seriousness"
                                    value={this.state.repairAdviceCategory ? this.state.repairAdviceCategory.toString() : ''}
                                    onChange={async (e) => this.handleChange(e.target.value, 'repairAdviceCategory')}
                                >
                                    <option value="noRepair">{this.props.t('common.selectItem', this.props.t('repairAdvice.eemua'))}</option>
                                    <option value="repairImmediately">{this.props.t('repairCategory.repairImmediately')}</option>
                                    <option value="repairInSixMonths">{this.props.t('repairCategory.repairInSixMonths')}</option>
                                    <option value="repairOnOccasion">{this.props.t('repairCategory.repairOnOccasion')}</option>
                                    <option value="repairNextRenovation">{this.props.t('repairCategory.repairNextRenovation')}</option>
                                    <option value="repairNextInspection">{this.props.t('repairCategory.repairNextInspection')}</option>
                                    <option value="noRepair">{this.props.t('repairCategory.noRepair')}</option>
                                </select>
                                <i className="arrow" />
                            </label>
                            {this.renderHelpText(this.getValidationMessages('repairAdviceCategory'))}
                        </div>
                        <div className="col-md-3">
                            <div className="section">
                                <label
                                    htmlFor="repairDate"
                                    className={this.getClassesWithDefault('repairDate', { field: true, 'prepend-icon': true })}
                                >
                                    <LocalizedDatePicker
                                        placeholder={this.props.t('repairAdvice.repairDate')}
                                        value={this.state.repairDate ?? null}
                                        onChange={async (value) => this.handleChange(value, 'repairDate')}
                                    />
                                </label>
                                {this.renderHelpText(this.getValidationMessages('repairDate'))}
                            </div>
                        </div>
                    </EditPanelRow>
                )}

                {/* Defect parameters */}
                {!isEemuaOrApi(this.props.inspectionStandardType) && (
                    <>
                        {isNen && this.state.code && !this.state.hideExtend && this.props.languageCode === 'nl' && (
                            <EditPanelRow>
                                <EditPanelCell rowCount={2}>
                                    <div className="mt10 ml10">
                                        <strong>{this.state.measuringUnit}:</strong>
                                    </div>
                                </EditPanelCell>
                                <EditPanelCell rowCount={2}>
                                    <EditPanelInput
                                        name="amount"
                                        placeholder={this.props.t('common.amount')}
                                        value={this.state.amount}
                                        restrictToNumbers={true}
                                        handleChange={this.handleChange.bind(this)}
                                    />
                                </EditPanelCell>
                                <EditPanelCell rowCount={1}>
                                    <div className="mt10">{this.state.measuringUnitAbbreviation}</div>
                                </EditPanelCell>
                                {this.state.measuringUnitAbbreviation !== '%' && (
                                    <>
                                        <EditPanelCell rowCount={2}>
                                            <div className="mt10">van een totaal van</div>
                                        </EditPanelCell>
                                        <EditPanelCell rowCount={2}>
                                            <EditPanelInput
                                                name="amountTotal"
                                                placeholder={this.props.t('common.totalAmount')}
                                                value={this.state.amountTotal}
                                                restrictToNumbers={true}
                                                handleChange={this.handleChange.bind(this)}
                                            />
                                        </EditPanelCell>
                                        <EditPanelCell rowCount={1}>
                                            <div className="mt10">{this.state.measuringUnitAbbreviation}</div>
                                        </EditPanelCell>
                                    </>
                                )}
                            </EditPanelRow>
                        )}
                        <EditPanelRow>
                            <EditPanelCell rowCount={2}>
                                <EditPanelSelect
                                    name="seriousness"
                                    placeholder={`${this.props.t('common.select')}...`}
                                    value={this.state.seriousness ? this.state.seriousness.toString() : ''}
                                    topLabel={this.props.t('defect.seriousness')}
                                    disabled={this.state.hideSeriousness}
                                    handleChange={this.handleChange.bind(this)}
                                    options={[
                                        { value: '1', text: this.props.t('seriousness.level1') },
                                        { value: '2', text: this.props.t('seriousness.level2') },
                                        { value: '3', text: this.props.t('seriousness.level3') },
                                    ]}
                                />
                            </EditPanelCell>
                            <EditPanelCell rowCount={3}>
                                <EditPanelSelect
                                    name="intensity"
                                    placeholder={`${this.props.t('common.select')}...`}
                                    value={this.state.intensity ? this.state.intensity.toString() : ''}
                                    topLabel={this.props.t('defect.intensity')}
                                    disabled={this.state.hideIntensity}
                                    handleChange={this.handleChange.bind(this)}
                                    options={[
                                        { value: '1', text: this.props.t('intensity.level1') },
                                        { value: '2', text: this.props.t('intensity.level2') },
                                        { value: '3', text: this.props.t('intensity.level3') },
                                    ]}
                                />
                            </EditPanelCell>
                            <EditPanelCell rowCount={4}>
                                <EditPanelSelect
                                    name="extend"
                                    placeholder={`${this.props.t('common.select')}...`}
                                    value={this.state.extend ? this.state.extend.toString() : ''}
                                    topLabel={this.props.t('defect.extend')}
                                    disabled={this.state.hideExtend}
                                    handleChange={this.handleChange.bind(this)}
                                    options={[
                                        { value: '1', text: this.props.t('extend.level1') },
                                        { value: '2', text: this.props.t('extend.level2') },
                                        { value: '3', text: this.props.t('extend.level3') },
                                        { value: '4', text: this.props.t('extend.level4') },
                                        { value: '5', text: this.props.t('extend.level5') },
                                    ]}
                                />
                            </EditPanelCell>
                            <EditPanelCell rowCount={3}>
                                <div className="mt30 mr10 pull-right">
                                    {(this.state.defectType === 'Tekortkoming' && <ConditionScoreLabel value={this.state.careScore} />) || (
                                        <ConditionScoreLabel value={this.state.score} />
                                    )}
                                </div>
                            </EditPanelCell>
                        </EditPanelRow>
                        {isNen && (
                            <>
                                <div className="hr-sect mb20 mt20">Additionele gegevens</div>
                                <EditPanelRow>
                                    <EditPanelCell rowCount={5}>
                                        <RamssheepDropdownNen
                                            name="ramsR"
                                            header={this.props.t('rams.reliability')}
                                            value={this.state.ramsR}
                                            topLabel="R"
                                            handleChange={this.handleChange.bind(this)}
                                            options={ramsROptions}
                                            subText={this.props.t('ramsDescription.reliability')}
                                        />
                                        <RamssheepDropdownNen
                                            name="ramsA"
                                            header={this.props.t('rams.availability')}
                                            value={this.state.ramsA}
                                            topLabel="A"
                                            handleChange={this.handleChange.bind(this)}
                                            options={ramsAOptions}
                                            subText={this.props.t('ramsDescription.availability')}
                                        />
                                        <RamssheepDropdownNen
                                            name="ramsS"
                                            header={this.props.t('rams.safety')}
                                            value={this.state.ramsS}
                                            topLabel="S"
                                            handleChange={this.handleChange.bind(this)}
                                            options={ramsSOptions}
                                            subText={this.props.t('ramsDescription.safety')}
                                        />
                                        <RamssheepDropdownNen
                                            name="ramsEc"
                                            header={this.props.t('rams.consequentialCosts')}
                                            value={this.state.ramsEc}
                                            topLabel="E"
                                            handleChange={this.handleChange.bind(this)}
                                            options={ramsEcOptions}
                                            subText={this.props.t('ramsDescription.consequentialCosts')}
                                        />
                                        <RamssheepDropdownNen
                                            name="ramsEnv"
                                            header={this.props.t('rams.environment')}
                                            value={this.state.ramsEnv}
                                            topLabel="E"
                                            handleChange={this.handleChange.bind(this)}
                                            options={ramsEnvOptions}
                                            subText={this.props.t('ramsDescription.environment')}
                                        />
                                    </EditPanelCell>
                                    <EditPanelCell rowCount={3}>
                                        {weightedPriorityValue !== '0' && (
                                            <div className="mt30">
                                                <span className={`label p5 fs12 rams_result${weightedPriorityValue}`}>
                                                    {' '}
                                                    {this.props.t(`ramsPriority.${weightedPriorityValue}`)}
                                                </span>
                                            </div>
                                        )}
                                    </EditPanelCell>
                                    <EditPanelCell rowCount={4}>
                                        <EditPanelSelect
                                            name="cause"
                                            placeholder={`${this.props.t('common.select')}...`}
                                            value={this.state.cause}
                                            topLabel={this.props.t('damage.cause')}
                                            handleChange={this.handleChange.bind(this)}
                                            options={[
                                                { value: 'Aanrijding', text: this.props.t('damage.collision') },
                                                { value: 'Carbonatatie', text: this.props.t('damage.carbonation') },
                                                { value: 'Corrosie', text: this.props.t('damage.corrosion') },
                                                { value: 'Lekkage', text: this.props.t('damage.leakage') },
                                                { value: 'Niet (juist) aangebracht', text: this.props.t('damage.applied') },
                                                { value: 'Niet (juist) uitgevoerd', text: this.props.t('damage.executed') },
                                                { value: 'Uitspoeling', text: this.props.t('damage.washout') },
                                                { value: 'Vandalisme', text: this.props.t('damage.vandalism') },
                                                { value: 'Verontreiniging', text: this.props.t('damage.pollution') },
                                                { value: 'Veroudering', text: this.props.t('damage.aging') },
                                                { value: 'Vervorming', text: this.props.t('damage.deformation') },
                                                { value: 'Verstopping', text: this.props.t('damage.blockage') },
                                                { value: 'Zettingen', text: this.props.t('damage.settlement') },
                                                { value: 'Anders', text: this.props.t('damage.other') },
                                            ]}
                                        />
                                    </EditPanelCell>
                                </EditPanelRow>
                                <EditPanelRow>
                                    <EditPanelCell rowCount={12}>
                                        <EditPanelInput
                                            name="locationDescription"
                                            value={this.state.locationDescription}
                                            handleChange={this.handleChange.bind(this)}
                                            topLabel={this.props.t('damage.location')}
                                        />
                                    </EditPanelCell>
                                </EditPanelRow>
                                <EditPanelRow>
                                    <EditPanelCell rowCount={12}>
                                        <EditPanelInput
                                            name="details"
                                            value={this.state.details}
                                            handleChange={this.handleChange.bind(this)}
                                            topLabel={this.props.t('damage.details')}
                                        />
                                    </EditPanelCell>
                                </EditPanelRow>
                                <EditPanelRow>
                                    <EditPanelCell rowCount={12}>
                                        <EditPanelInput
                                            name="repairAdvice"
                                            value={this.state.repairAdvice}
                                            handleChange={this.handleChange.bind(this)}
                                            topLabel={this.props.t('damage.advice')}
                                        />
                                    </EditPanelCell>
                                </EditPanelRow>
                            </>
                        )}
                    </>
                )}
                <EditPanelButtons>
                    <EditPanelButton onClick={() => this.onCancelClicked()} text={this.props.t('common.cancel')} small={false} />
                    <EditPanelButton
                        onClick={() => this.onSaveClicked()}
                        text={this.props.t('common.save')}
                        small={false}
                        primary={true}
                        disabled={isNen && (!this.state.seriousness || !this.state.intensity || !this.state.extend)}
                    />
                </EditPanelButtons>
            </EditPanel>
        );
    }

    protected override async onChanged(value: any, stateKey: string) {
        const newState = {} as any;
        newState[stateKey] = value;

        if (stateKey === 'code' && value !== 'custom') {
            const nenDefect = this.getNenDefect(value);
            if (nenDefect) {
                if (nenDefect.seriousness === 'gering') {
                    newState.seriousness = 1;
                    newState.hideSeriousness = true;
                } else if (nenDefect.seriousness === 'serieus') {
                    newState.seriousness = 2;
                    newState.hideSeriousness = true;
                } else if (nenDefect.seriousness === 'ernstig') {
                    newState.seriousness = 3;
                    newState.hideSeriousness = true;
                }

                if (nenDefect.intensity === 'Beginstadium') {
                    newState.intensity = 1;
                    newState.hideIntensity = true;
                } else if (nenDefect.intensity === 'Gevorderd Stadium') {
                    newState.intensity = 2;
                    newState.hideIntensity = true;
                } else if (nenDefect.intensity === 'Eindstadium') {
                    newState.intensity = 3;
                    newState.hideIntensity = true;
                }

                newState.code = nenDefect.code;
                newState.name = nenDefect.name;
                newState.defectType = nenDefect.defectType;
                newState.amount = undefined;
                newState.amountTotal = undefined;
                newState.description = nenDefect.description;
                newState.measuringUnit = nenDefect.unit;
                newState.measuringUnitAbbreviation = nenDefect.unitAbbreviation;
            }
        }
        if (stateKey === 'code' && value === 'custom') {
            newState.code = '';
            newState.name = '';
            newState.defectType = 'custom';
            newState.description = '';
            newState.hideSeriousness = false;
            newState.hideIntensity = false;
            newState.hideExtend = false;
        }

        let calculatedPrcentage = 0;
        if (stateKey === 'amount') {
            const amountTotal = this.state.amountTotal ?? 0;
            newState.amount = Math.round(value ?? 0);
            if (value > 0 && amountTotal > 0) {
                calculatedPrcentage = (value / amountTotal) * 100;
            }
            if (this.state.measuringUnitAbbreviation === '%') {
                calculatedPrcentage = value;
            }
        }
        if (stateKey === 'amountTotal') {
            const amount = this.state.amount ?? 0;
            newState.amountTotal = Math.round(value ?? 0);
            if (value > 0 && amount > 0) {
                calculatedPrcentage = (amount / value) * 100;
            }
        }

        if (calculatedPrcentage > 0) {
            if (calculatedPrcentage > 70) {
                newState.extend = '5';
            } else if (calculatedPrcentage > 30) {
                newState.extend = '4';
            } else if (calculatedPrcentage > 10) {
                newState.extend = '3';
            } else if (calculatedPrcentage >= 2) {
                newState.extend = '2';
            } else {
                newState.extend = '1';
            }
        }

        await this.setStatePromised(newState);
        await this.setDefectScores();
    }

    private nenDefectsToOptions(nenDefects: INenDefect[]): any[] {
        const result = [] as any[];

        result.push({ value: 'custom', label: this.props.t('defect.customDefect') });
        for (let index = 0; index < nenDefects.length; index++) {
            const nenDefect = nenDefects[index]!;
            if (nenDefect.description) {
                result.push({ value: nenDefect.code, label: `${nenDefect.code}: ${nenDefect.name}. ${nenDefect.description}` });
            } else {
                result.push({ value: nenDefect.code, label: `${nenDefect.code}: ${nenDefect.name}` });
            }
        }
        return result;
    }

    private getNenDefect(code?: string): INenDefect | undefined {
        for (let index = 0; index < this.props.nenDefects.length; index++) {
            const nenDefect = this.props.nenDefects[index]!;
            if (nenDefect.code === code) {
                return nenDefect;
            }
        }
        return undefined;
    }

    private onSaveClicked() {
        this.validate((validationError: any) => {
            if (!validationError) {
                const data = this.getValidatorData();
                this.props.onSave(data);
            }
        });
    }

    private onCancelClicked() {
        this.props.onCancel();
    }

    private async setDefectScores() {
        if (this.state.seriousness && this.state.intensity && this.state.extend) {
            if (this.props.useCareScore) {
                if (this.state.defectType === 'Tekortkoming') {
                    await this.setStatePromised({
                        score: 1,
                        careScore: calculateDefectScore(this.state.seriousness, this.state.intensity, this.state.extend),
                    });
                } else {
                    await this.setStatePromised({
                        careScore: 1,
                        score: calculateDefectScore(this.state.seriousness, this.state.intensity, this.state.extend),
                    });
                }
            } else {
                await this.setStatePromised({ score: calculateDefectScore(this.state.seriousness, this.state.intensity, this.state.extend) });
            }
        }
    }
}

// eslint-disable-next-line import/no-default-export
export default translate()(connect(mapStateToProps, undefined, undefined, { forwardRef: true })(DefectAddEdit as any) as any) as any;
